import { type ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands';
import {
  serializeDatabase,
  type ConfluencePageConnectionResolver,
  type JiraIssueDetailsResolver
} from '@databases/ai-first-creation-serializer';
import type { DatabaseEntryFieldValuesSnapshot } from '@databases/ai-first-creation-serializer/database-entry-field-values-snapshot';
import { collabStateManager } from '@databases/collab-state-manager';
import { selectContentPropertyOptional } from '@databases/connections-api/selectors';
import { viewState } from '@databases/database-state/state/view';
import { logError } from '@databases/logs';
import { type Database } from '@databases/model/database';
import { FieldType } from '@databases/model/field';
import { type Value } from '@databases/model/value';
import { type DefaultRootState } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { appStore } from '@databases/redux/store';
import { getService } from '@databases/service-container';
import { getJiraColumnTextValues } from '@databases/utils/database/field-types/jiraIssueDetails';
import { getDefaultView } from '@databases/utils/database/view/view';
import {
  selectDatabaseByContentIdRequired,
  selectDatabaseNameByContentIdOptional
} from '@databases/utils/redux/databases/selectors';
import { selectJiraIssueOptional } from '@databases/utils/redux/jira/selectors';
import { snapshot } from 'valtio';

import { DatabaseStreamState } from '../database-stream-state';
import { serializeSelection } from '../utils/serialize-selection';

const streamState = DatabaseStreamState.getInstance();

export function createJiraIssueDetailsResolver(
  database: Database,
  state: DefaultRootState
): JiraIssueDetailsResolver {
  return (entry, field) => {
    const parentFieldId = field.fieldId;
    const jiraColumnName = field.jiraField;
    if (!parentFieldId || !jiraColumnName) {
      return '';
    }

    const parentField = database.fieldsById[parentFieldId];
    if (!parentField) {
      return '';
    }

    const parentValue = entry.getValue(parentFieldId);
    if (!parentValue || parentValue.type !== FieldType.JiraIssue) {
      return '';
    }

    return (parentValue as Value<FieldType.JiraIssue>).value
      .flatMap((issueRef) => {
        if (!issueRef.key) {
          return [];
        }
        const resolved = selectJiraIssueOptional(state, {
          key: issueRef.key,
          cloudId: parentField.cloudId,
          serverId: parentField.jiraServerId
        });
        if (!resolved) {
          return [];
        }
        const columnData = resolved.columns[jiraColumnName];
        if (!columnData) {
          return [];
        }
        return getJiraColumnTextValues(columnData);
      })
      .join('|');
  };
}

function resolvePageLinkColumnTexts<T>(
  pageLinks: Value<FieldType.PageLink>['value'],
  state: DefaultRootState['connections'],
  property: string,
  resolveTexts: (data: T) => string[]
): string {
  return pageLinks
    .flatMap((link) => {
      const ari = link.ari;
      if (!ari) {
        return [];
      }
      const data = selectContentPropertyOptional(state, ari, property)?.data as
        | T
        | undefined;
      return data ? resolveTexts(data) : [];
    })
    .join('|');
}

export function createConfluencePageConnectionResolver(
  state: DefaultRootState
): ConfluencePageConnectionResolver {
  return (entry, field) => {
    const parentFieldId = field.fieldId;
    if (!parentFieldId) {
      return '';
    }

    const parentValue = entry.getValue(parentFieldId);
    if (!parentValue || parentValue.type !== FieldType.PageLink) {
      return '';
    }

    const pageLinks = (parentValue as Value<FieldType.PageLink>).value;

    if (field.type === FieldType.PageLabels) {
      return resolvePageLinkColumnTexts<Array<{ text: string }>>(
        pageLinks,
        state.connections,
        'labels',
        (data) => data.map((label) => label.text)
      );
    }

    if (field.type === FieldType.PageStatus) {
      return resolvePageLinkColumnTexts<{ text: string; color: string }>(
        pageLinks,
        state.connections,
        'pageStatus',
        (data) => [data.text]
      );
    }

    return '';
  };
}

type ReadDatabaseContentOptions = {
  includeAiFields?: boolean;
  includeSelection?: boolean;
  populateStreamMappings?: boolean;
  entryFieldValuesSnapshot?: DatabaseEntryFieldValuesSnapshot;
  entryScope?:
    | { kind: 'all-entries' }
    | { kind: 'empty-ai-summary-entries'; fieldId: string }
    | { kind: 'specific-entries'; entryIds: readonly string[] };
};

export const readDatabaseContent = (
  contentId: string,
  {
    includeAiFields = false,
    includeSelection = true,
    populateStreamMappings = true,
    entryFieldValuesSnapshot,
    entryScope = { kind: 'all-entries' }
  }: ReadDatabaseContentOptions = {}
): ReadContentCommandResult => {
  try {
    const state = appStore.getState();
    const databaseName = selectDatabaseNameByContentIdOptional(state, { contentId });

    const manager = collabStateManager.getCollabManager(contentId);

    if (!manager) {
      throw new Error('Database not found');
    }

    const currentView = snapshot(viewState.view);
    const currentViewId = currentView.id || undefined;
    const reader = getService('SelectionsReader');
    const permissions = getService('PermissionsManager').getPermissions();
    // AI field runs target every row, so incidental UI selection must not scope the context.
    const selectedCells = includeSelection
      ? serializeSelection({
          selectedEntireFields: reader.getSelectedFieldIds(),
          selectedEntireEntries: reader.getSelectedEntryIds(),
          selectedValues: reader.getSelectedValues(),
          database: manager.database
        })
      : new Set<string>();

    const database = selectDatabaseByContentIdRequired(state, { contentId });
    const view =
      (currentViewId && database.views.find((v) => v.id === currentViewId)) ||
      getDefaultView(database);

    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: false,
      viewSettings: view?.settings
    });
    const entryIds = (() => {
      switch (entryScope.kind) {
        case 'all-entries':
          return databaseDraft.entries;
        case 'empty-ai-summary-entries':
          return databaseDraft.entries.filter((entryId) => {
            const value = manager.database.entriesById[entryId]?.getValue(
              entryScope.fieldId
            );

            return (
              value === undefined || (value.type === FieldType.AiSummary && value.isEmpty)
            );
          });
        case 'specific-entries':
          return entryScope.entryIds;
        default: {
          const unhandledScope: never = entryScope;
          throw new Error(`Unhandled database entry scope: ${String(unhandledScope)}`);
        }
      }
    })();

    const {
      output: csv,
      warnings,
      rowIdToEntryIdMappings
    } = serializeDatabase(manager.database, selectedCells, {
      currentViewId,
      entryIds,
      fieldIds: entryFieldValuesSnapshot?.fieldIds ?? databaseDraft.fields,
      includeAiFields,
      entryFieldValuesSnapshot,
      resolveJiraIssueDetails: createJiraIssueDetailsResolver(manager.database, state),
      resolveConfluencePageConnection: createConfluencePageConnectionResolver(state)
    });

    if (warnings.hasWarnings()) {
      logError('Warnings during database serialization', new Error(warnings.toString()));
    }

    const title = databaseName || 'Untitled Database';
    const url = globalThis.location.href;

    // Read-only AI field previews parse responses locally and must not replace edit-stream mappings.
    if (populateStreamMappings) {
      streamState.populateRowIdToEntryIdMappings(rowIdToEntryIdMappings);
    }

    return {
      contentType: 'database',
      csv,
      title,
      url,
      contentId,
      isViewMode: !permissions.canEditContent && !permissions.canEditFields
    } satisfies ReadContentCommandResult;
  } catch (possibleError) {
    const error =
      possibleError instanceof Error ? possibleError : new Error(String(possibleError));
    logError('Error reading database content', error);
    throw error;
  }
};
